import React, { useContext, useEffect, useRef, useState } from "react"
import { ActivityIndicator, ScrollView, Text, View, TouchableOpacity } from "react-native"
import { ThemeContext } from "../utility/context/ThemeContext"
import { textStyles } from "../style"
import api from "../api-client"
import { Ionicons, MaterialCommunityIcons } from "@expo/vector-icons"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { MAX_CONTENT_WIDTH } from "../config"
import { StackActions, useNavigation } from "@react-navigation/native"
import Analytics from "../utility/analytics"
import ThemedDialog from "../style/dialog"
import CircularCountdownTimer, { CountdownTimerRef } from "../utility/circular-countdown-timer"

type Props = {
  route: {
    params: {
      token?: string
      state?: string
      theme?: string
      email?: string
    }
  }
}

const EmailVerification = ({ route }: Props): React.ReactElement<Props> => {
  const { theme, setTheme } = useContext(ThemeContext)
  const { textMain, bgMain, success } = theme.colors
  const navigation = useNavigation()
  const { top, bottom } = useSafeAreaInsets()

  const [loading, setLoading] = useState<boolean>(false)
  const [redirecting, setRedirecting] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [numResends, setNumResends] = useState<number>(0)
  const [showNoCodeModal, setShowNoCodeModal] = useState<boolean>(false)
  const [resendLocked, setResendLocked] = useState<boolean>(true)
  const [showManualContinue, setShowManualContinue] = useState<boolean>(false)
  const timerRef = useRef<CountdownTimerRef>(null)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollInFlightRef = useRef<boolean>(false)
  const manualContinueTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const screenStartTsRef = useRef<number>(Date.now())

  const INITIAL_TIMER_DURATION = 30_000
  const SECOND_TIMER_DURATION = 60_000
  const MAX_RESENDS_BEFORE_MODAL = 3
  const POLL_INTERVAL_MS = 3_000
  const MANUAL_CONTINUE_AFTER_MS = 15_000

  // handle params
  useEffect(() => {
    const params = route.params
    if (params?.theme) {
      setTheme(params.theme)
    }
    if (!params?.token || !params?.email) {
      navigation.dispatch(StackActions.replace("Login"))
    }
  }, [navigation, route.params, setTheme])

  // Always show manual "here" after 15 seconds, regardless of verification state.
  useEffect(() => {
    screenStartTsRef.current = Date.now()
    setShowManualContinue(false)

    if (manualContinueTimeoutRef.current) {
      clearTimeout(manualContinueTimeoutRef.current)
    }

    manualContinueTimeoutRef.current = setTimeout(() => {
      setShowManualContinue(true)
    }, MANUAL_CONTINUE_AFTER_MS)

    return () => {
      if (manualContinueTimeoutRef.current) {
        clearTimeout(manualContinueTimeoutRef.current)
      }
    }
  }, [route.params?.token])

  const buildContinueUrl = (redirect_uri: string): string => {
    const params = new URLSearchParams()
    if (route.params?.state) params.set("state", route.params.state)
    if (route.params?.token) params.set("token", route.params.token)
    params.set("status", "success")
    return `${redirect_uri}?${params.toString()}`
  }

  const checkVerifiedAndContinue = async (opts?: { source: "poll" | "manual" }) => {
    if (!route.params?.token) return
    if (pollInFlightRef.current) return

    pollInFlightRef.current = true
    const source = opts?.source ?? "poll"
    const isManual = source === "manual"
    if (isManual) {
      setError(undefined)
    }

    try {
      api.setToken(route.params?.token)
      const { redirect_uri } = await api.checkEmailVerified(route.params?.state)

      Analytics.track("email_verified_check", { success: true, source })

      // Stop polling once we know they're verified and we're attempting redirect.
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }

      setRedirecting(true)

      window.location.href = buildContinueUrl(redirect_uri)
    } catch (e: any) {
      Analytics.track("email_verified_check", { success: false, source })

      // Polling should be silent until verified; manual click can show feedback.
      const status = e?.status
      const isNotYetVerified = status === 403
      if (!isManual && isNotYetVerified) return

      const message =
        e?.message ||
        "We haven't detected your email verification yet. Please check your inbox and try again."
      setError(message)
    } finally {
      pollInFlightRef.current = false
    }
  }

  // Automatic polling every 3 seconds.
  useEffect(() => {
    if (!route.params?.token) return

    setRedirecting(false)

    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
    pollIntervalRef.current = setInterval(() => {
      checkVerifiedAndContinue({ source: "poll" })
    }, POLL_INTERVAL_MS)

    // Kick off immediately.
    checkVerifiedAndContinue({ source: "poll" })

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.params?.token, route.params?.state])

  const handleManualContinuePress = () => {
    const elapsedMs = Date.now() - screenStartTsRef.current
    Analytics.track("email_verification_manual_continue_pressed", {
      elapsed_ms: elapsedMs,
      redirecting,
    })
    checkVerifiedAndContinue({ source: "manual" })
  }

  const handleResend = async () => {
    setLoading(true)
    setResendLocked(true)
    try {
      api.setToken(route.params?.token)
      await api.resendEmailVerification()
      Analytics.track('email_resend_verification', { num_resends: numResends + 1 })
      timerRef.current?.resetTimer(SECOND_TIMER_DURATION, true)
      setNumResends(numResends + 1)
    } catch (e: any) {
      setError(e?.message || "An error occurred while resending the verification email")
    } finally {
      setLoading(false)
    }
  }

  return (
    <ScrollView
      style={{
        flex: 1,
        backgroundColor: bgMain,
      }}
      contentContainerStyle={{
        flexGrow: 1,
        justifyContent: "center",
        alignItems: "center",
        paddingTop: top || 16,
        paddingBottom: bottom || 16,
        paddingHorizontal: 16,
      }}
      keyboardShouldPersistTaps="handled"
    >
      <View style={{ width: "100%", maxWidth: MAX_CONTENT_WIDTH }}>
        <Text style={[textStyles.h3, { color: textMain, textAlign: "center" }]}>
          Verify your email to continue
        </Text>

        <View style={{ width: "100%", alignItems: "center", marginTop: 12 }}>
          <MaterialCommunityIcons name="email-check-outline" color={textMain} size={150} />
        </View>

        <Text style={[textStyles.body, { color: textMain, textAlign: "center", marginTop: 12 }]}>
          We have sent a verification email to {route.params?.email}.{'\n'}
          Once you&apos;ve verified your email, we&apos;ll automatically continue.
        </Text>

        {redirecting ? (
          <View style={{ alignItems: "center", marginTop: 12 }}>
            <ActivityIndicator color={textMain} />
            <Text style={[textStyles.body, { color: textMain, textAlign: "center", marginTop: 8 }]}>
              Email verified. Continuing…
            </Text>
          </View>
        ) : null}

        {/* Resend section with timer */}
        <View style={{
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          gap: 8,
          marginTop: 8
        }}>
          <CircularCountdownTimer
            ref={timerRef}
            initialTimerDuration={INITIAL_TIMER_DURATION}
            startAutomatically
            width={20}
            height={20}
            strokeColor={textMain}
            strokeWidth={1}
            backgroundColor={'transparent'}
            foregroundColor={textMain}
            clockwise
            onTimerEnd={() => {
              setResendLocked(numResends >= MAX_RESENDS_BEFORE_MODAL)
              if (numResends >= MAX_RESENDS_BEFORE_MODAL) {
                setShowNoCodeModal(true)
              }
            }}
          />
          <TouchableOpacity onPress={handleResend} disabled={resendLocked || loading}>
            <Text style={[
              textStyles.h4,
              { color: textMain, opacity: (resendLocked || loading) ? 0.5 : 1 }
            ]}>
              Resend email
            </Text>
          </TouchableOpacity>
        </View>

        {showManualContinue ? (
          <Text style={[textStyles.body, { color: textMain, textAlign: "center", marginTop: 8 }]}>
            If you&apos;re not redirected automatically after verifying your email, tap{" "}
            <Text
              onPress={handleManualContinuePress}
              style={{ textDecorationLine: "underline" }}
            >
              here
            </Text>{" "}
            to continue.
          </Text>
        ) : null}

        {error ? (
          <Text style={[textStyles.body, { color: theme.colors.danger, textAlign: "center", marginTop: 12 }]}>
            {error}
          </Text>
        ) : null}
      </View>

      {/* Error/Help Modal similar to MFA */}
      <ThemedDialog
        visible={showNoCodeModal}
        title="Still no email?"
        subMessage={
          "We're sorry for the inconvenience. We care about your experience, " +
          "and our team has been notified to look into this right away."
        }
        errorAnimation={showNoCodeModal}
        options={[
          {
            title: 'Okay',
            onPress: () => {
              setShowNoCodeModal(false)
              setResendLocked(true)
            },
            role: 'confirm',
            icon: <Ionicons name="checkmark" size={22} />
          }
        ]}
      />
    </ScrollView>
  )
}

export default EmailVerification

